import { Stack, Box, Typography } from "@mui/material";
import CustomTable from "global/components/CustomTable/CustomTable";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { deleteCard, getPaymentMethods, setDefaultCard } from "../PlanService";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import DeleteIcon from "assets/icons1/DeleteIcon.svg";
import { useEffect, useState } from "react";
import ConfirmationModal from "screens/Templates/Components/ConfirmationModal";

interface CustomProps {
  doesNewPaymentMethodAdded: boolean;
  setIsLoading: Function;
  isLoading: boolean;
}

const paymentMethodsTableHeader = [
  {
    name: "Card Number",
    field: "number",
    align: "left",
    sorting: false,
  },
  {
    name: "Delete",
    field: "delete",
    align: "right",
    sorting: false,
    hide: true,
  },
];

const PaymentMethods = (props: CustomProps) => {
  const { setIsLoading, isLoading } = props;
  const [paymentMethods, setPaymentMethods] = useState<any>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [cardAsPrimaryModal, setCardAsPrimaryModal] = useState<boolean>(false);
  const [cardId, setCardId] = useState<string>("");
  const [selected, setSelected] = useState<string>("");
  const [previousSelected, setPreviousSelected] = useState<string>("");

  useEffect(() => {
    fetchPaymentMethods();
  }, [props.doesNewPaymentMethodAdded]);

  const fetchPaymentMethods = async () => {
    try {
      setIsLoading(true);
      const paymentMethodsRes = await getPaymentMethods();
      const paymentMethodDetails = convertTableData(paymentMethodsRes);
      const defaultCard = paymentMethodsRes.find((card: any) => card.default);
      setSelected(defaultCard?.id || "");
      setPaymentMethods(paymentMethodDetails);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const deleteCardHandler = async (cardId: string) => {
    try {
      setIsLoading(true);
      setOpenDeleteModal(false);
      const res = await deleteCard(cardId);
      openSuccessNotification(res.message ?? "Delete Card Successfully");
      await fetchPaymentMethods();
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const setDefaultCardHandler = async () => {
    try {
      setIsLoading(true);
      setCardAsPrimaryModal(false);
      const res = await setDefaultCard(selected);
      openSuccessNotification(
        res.message ?? "Card set as default successfully"
      );
      await fetchPaymentMethods();
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      setCardAsPrimaryModal(false);
      handleErrorMessage(error);
    }
  };

  const convertTableData = (data: any[]) => {
    return data.map((item: any) => ({
      rowId: item.id,
      number: (
        <Typography
          sx={{ color: "#475569" }}
          variant="subtitle2"
        >{`XXXX-XXXX-XXXX-${item.number}`}</Typography>
      ),
      delete: (
        <img
          style={{ cursor: "pointer" }}
          src={DeleteIcon}
          onClick={() => {
            setOpenDeleteModal(true);
            setCardId(item.id);
          }}
          alt="Delete icon"
        />
      ),
    }));
  };

  const handleSelectPrimaryCard = (selectedId: string) => {
    setPreviousSelected(selected);
    setSelected(selectedId);
    setCardAsPrimaryModal(true);
  };

  const handleCancelSetDefault = () => {
    setSelected(previousSelected);
    setCardAsPrimaryModal(false);
  };

  const getContactCampaigns = () => {
    return (
      <Stack direction="column" spacing={2}>
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={paymentMethodsTableHeader}
              rows={paymentMethods}
              showPagination={false}
              isLoading={isLoading}
              radioSelection={true}
              radioHeader="Primary Card"
              selectedRadio={selected}
              setSelectedRadio={(selectedId: string) =>
                handleSelectPrimaryCard(selectedId)
              }
            />
          </Box>
        </CenteredCardLayout>
      </Stack>
    );
  };

  return (
    <>
      {getContactCampaigns()}
      {openDeleteModal && (
        <ConfirmationModal
          isVisible={openDeleteModal}
          handleDialogClose={() => setOpenDeleteModal(false)}
          title="Please Confirm"
          subtitle2="Do you really want to remove this card?"
          btn1Title="Delete"
          btn1Variant="outlined"
          btn2Title="Cancel"
          btn2Variant="contained"
          btn1OnClick={() => deleteCardHandler(cardId)}
          btn2OnClick={() => setOpenDeleteModal(false)}
        />
      )}
      {cardAsPrimaryModal && (
        <ConfirmationModal
          isVisible={cardAsPrimaryModal}
          title={"Set as primary card"}
          subtitle2="Are you sure you want to set your card primary ?"
          btn1OnClick={() => handleCancelSetDefault()}
          btn2OnClick={() => setDefaultCardHandler()}
          handleDialogClose={() => handleCancelSetDefault()}
        />
      )}
    </>
  );
};

export default PaymentMethods;
